import { useEffect, useRef, useState, useCallback } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAssessment } from '@/context';
import { useAuth } from '@/context/AuthContext';
import { apiClient } from '@/lib/apiClient';
import {
  LEADERSHIP_FAMILIES,
  LEADERSHIP_TYPES,
  EQ_PILLARS,
  CULTURE_DIMENSIONS,
} from '@/types';
import type { AssessmentResult, LeadershipFamilyCode, LeadershipTypeCode } from '@/types/equip360';
import {
  getMetricPercentage,
  getCultureRippleInsight,
  getBEDProfileInsight,
  getPressurePatternInsight,
  getMoveTheStoolInsight,
  getBecauseStatementInsight,
  getEQPillarInsight,
  getCultureDimensionInsight,
} from '@/utils';
import {
  saveAssessment,
  getOrganizationContext,
  arrayToScores,
  sendAssessmentNotification,
} from '@/services/assessments';
import { useScrollReveal, useScrollProgress } from '@/hooks/useScrollReveal';
import { useCountUp } from '@/hooks/useCountUp';
import {
  getTierLabel,
  getTierChipClass,
  getTierColor,
  getBarGradient,
  BENCHMARKS,
  EQ_BENCHMARKS,
  computeGapMeter,
  getDialDashoffset,
  getArcDashoffset,
  getDonutDashoffset,
  getROICards,
  getProgressionData,
  getLeadershipStory,
  getScoresConnectorInsight,
  getBEDStoryInsight,
  getEQPatternInsight,
  getPressureShadowName,
  getPressureAntidote,
  getMTSHeadline,
  getActionItems,
} from '@/utils/v2ReportData';
import './ResultsPage.css';

function ResultsPage() {
  const navigate = useNavigate();
  const { id: assessmentId } = useParams<{ id: string }>();
  const { state } = useAssessment();
  const { result: contextResult, user } = state;
  const { user: authUser } = useAuth();

  const [loadedResult, setLoadedResult] = useState<AssessmentResult | null>(null);
  const [loadedName, setLoadedName] = useState<{ firstName: string; lastName: string } | null>(null);
  const [loadingFromDb, setLoadingFromDb] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [heroVisible, setHeroVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [persistedId, setPersistedId] = useState<string | null>(null);
  const hasSaved = useRef(false);
  const hasGeneratedPdf = useRef(false);
  const isFreshAssessment = !!contextResult;
  const isPrintMode = new URLSearchParams(window.location.search).get('print') === '1';

  const result = contextResult || loadedResult;

  const observe = useScrollReveal();
  useScrollProgress();

  const hasAttemptedLoad = useRef(false);

  useEffect(() => {
    if (contextResult || loadedResult || loadingFromDb || hasAttemptedLoad.current) return;
    if (!assessmentId) {
      navigate('/');
      return;
    }
    hasAttemptedLoad.current = true;
    setLoadingFromDb(true);
    const fetchFn = authUser
      ? apiClient.assessments.getById(assessmentId)
      : apiClient.assessments.getPublic(assessmentId);
    fetchFn.then((data) => {
      const scores = arrayToScores(data.scores);
      setLoadedResult({
        id: data.id,
        sessionId: '',
        userId: data.userId || '',
        scores,
        leadershipFamily: data.leadershipFamily as LeadershipFamilyCode,
        leadershipType: data.leadershipType as LeadershipTypeCode,
        completedAt: new Date(data.completedAt || ''),
      });
      if (data.firstName || data.lastName) {
        setLoadedName({ firstName: data.firstName || '', lastName: data.lastName || '' });
      }
    }).catch(() => {
      setLoadError('This assessment could not be found. It may have been removed or the link is incorrect.');
    }).finally(() => {
      setLoadingFromDb(false);
    });
  }, [assessmentId, authUser, contextResult, loadedResult, loadingFromDb, navigate]);

  useEffect(() => {
    if (!isFreshAssessment || !result || hasSaved.current) return;
    hasSaved.current = true;
    const saveResultToDb = async () => {
      if (authUser) {
        const organizationId = getOrganizationContext()?.organizationId;
        const saveResult = await saveAssessment({
          userId: authUser.id,
          result,
          organizationId,
          responses: state.session?.responses,
          firstName: user?.firstName || authUser?.firstName || '',
          lastName: user?.lastName || authUser?.lastName || '',
        });
        if (!saveResult.success) {
          hasSaved.current = false;
          console.warn('Failed to save assessment:', saveResult.error);
        } else if (saveResult.assessmentId) {
          const dbId = saveResult.assessmentId;
          if (dbId !== assessmentId) {
            window.history.replaceState(null, '', `/results/${dbId}`);
          }
          setPersistedId(dbId);
        }
      } else {
        try {
          const scoresArray = [
            result.scores.eq.SA, result.scores.eq.SR, result.scores.eq.M, result.scores.eq.E, result.scores.eq.SS,
            result.scores.bed.B, result.scores.bed.EX, result.scores.bed.D,
            result.scores.culture.T, result.scores.culture.PS, result.scores.culture.CQ, result.scores.culture.TS, result.scores.culture.ER,
          ];
          const saved = await apiClient.assessments.saveAnonymous({
            scores: scoresArray,
            leadershipFamily: result.leadershipFamily,
            leadershipType: result.leadershipType,
            responses: state.session?.responses ?? null,
            firstName: user?.firstName || '',
            lastName: user?.lastName || '',
          });
          if (saved.id) {
            if (saved.id !== assessmentId) {
              window.history.replaceState(null, '', `/results/${saved.id}`);
            }
            setPersistedId(saved.id);
          }
        } catch (err) {
          hasSaved.current = false;
          console.warn('Failed to save anonymous assessment:', err);
        }
      }
    };
    saveResultToDb();
  }, [result, authUser, state.session?.responses]);

  const effectiveId = isFreshAssessment ? persistedId : assessmentId;

  useEffect(() => {
    if (!isFreshAssessment || !result || !persistedId || hasGeneratedPdf.current || isPrintMode) return;
    const email = user?.email || authUser?.email;
    if (!email) return;
    hasGeneratedPdf.current = true;
    const firstName = user?.firstName || authUser?.firstName || '';
    const lastName = user?.lastName || authUser?.lastName || '';
    const orgContext = getOrganizationContext();
    const processPostAssessment = async () => {
      let pdfBase64: string | undefined;
      try {
        const resp = await fetch(`/api/pdf-base64/${persistedId}`, { method: 'POST' });
        if (resp.ok) {
          const data = await resp.json();
          if (data.success && data.base64) {
            pdfBase64 = data.base64;
          }
        }
      } catch { /* Server PDF generation failed */ }
      const MAX_BASE64_SIZE = 3500000;
      const base64ForUpload = pdfBase64 && pdfBase64.length <= MAX_BASE64_SIZE ? pdfBase64 : undefined;
      const scores = [
        result.scores.eq.SA, result.scores.eq.SR, result.scores.eq.M, result.scores.eq.E, result.scores.eq.SS,
        result.scores.bed.B, result.scores.bed.EX, result.scores.bed.D,
        result.scores.culture.T, result.scores.culture.PS, result.scores.culture.CQ, result.scores.culture.TS, result.scores.culture.ER,
      ];
      sendAssessmentNotification({
        userName: `${firstName} ${lastName}`.trim() || 'Unknown',
        userEmail: email,
        leadershipFamily: result.leadershipFamily,
        leadershipType: result.leadershipType,
        scores,
        organizationId: orgContext?.organizationId,
        organizationName: orgContext?.organizationName,
        company: user?.company,
        jobTitle: user?.role,
        pdfBase64: base64ForUpload,
      });
    };
    processPostAssessment();
  }, [isFreshAssessment, result, persistedId, user, authUser, isPrintMode]);

  const handleDownloadPDF = useCallback(async () => {
    if (generatingPdf || !result || !effectiveId) return;
    setGeneratingPdf(true);
    try {
      const firstName = user?.firstName || authUser?.firstName || 'User';
      const lastName = user?.lastName || authUser?.lastName || '';
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `EQUIP360-${firstName}-${lastName}-${dateStr}.pdf`;

      const response = await fetch(`/api/pdf/${effectiveId}`);
      if (!response.ok) throw new Error(`PDF generation failed: ${response.status}`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
    } finally {
      setGeneratingPdf(false);
    }
  }, [generatingPdf, user, authUser, result, effectiveId]);

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (loadError) {
    return (
      <div className="v2-loading">
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>&#9888;</div>
        <p style={{ maxWidth: '400px', textAlign: 'center', lineHeight: 1.6 }}>{loadError}</p>
        <button
          onClick={() => navigate('/')}
          style={{ marginTop: '24px', padding: '12px 32px', background: 'var(--v2-gold, #E2C068)', color: '#000', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 }}
        >
          Take an Assessment
        </button>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="v2-loading">
        <div className="v2-loading-spinner" />
        <p>Loading your leadership intelligence report...</p>
      </div>
    );
  }

  const leadershipFamily = LEADERSHIP_FAMILIES[result.leadershipFamily];
  const leadershipType = LEADERSHIP_TYPES[result.leadershipType];
  const { scores } = result;
  const overallPct = scores.overall.percentage;
  const eqPct = scores.eq.percentage;
  const bedPct = scores.bed.percentage;
  const ciPct = scores.culture.percentage;

  const firstName = user?.firstName || authUser?.firstName || loadedName?.firstName || '';
  const lastName = user?.lastName || authUser?.lastName || loadedName?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();
  const dateStr = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  const leadershipStory = getLeadershipStory(scores, result.leadershipFamily, result.leadershipType);
  const gapData = computeGapMeter(scores);
  const scoresConnector = getScoresConnectorInsight(scores);
  const eqPattern = getEQPatternInsight(scores);
  const bedInsights = getBEDProfileInsight(scores, result.leadershipType);
  const bedStory = getBEDStoryInsight(scores);
  const roiCards = getROICards(scores);
  const cultureRipple = getCultureRippleInsight(scores, result.leadershipFamily);
  const pressureInsight = getPressurePatternInsight(scores, result.leadershipType);
  const shadowName = getPressureShadowName(result.leadershipType);
  const antidote = getPressureAntidote(result.leadershipType);
  const progressionData = getProgressionData(overallPct);
  const mtsHeadline = getMTSHeadline(scores, result.leadershipType);
  const moveTheStool = getMoveTheStoolInsight(scores, result.leadershipType);
  const becauseStatement = getBecauseStatementInsight(scores, result.leadershipType, result.leadershipFamily);
  const actionItems = getActionItems(scores, result.leadershipType, result.leadershipFamily);

  const circumference104 = 2 * Math.PI * 104;
  const circumference52 = 2 * Math.PI * 52;
  const circumference30 = 2 * Math.PI * 30;

  const eqPillarData = (['SA', 'SR', 'M', 'E', 'SS'] as const).map((code) => {
    const raw = scores.eq[code] as number;
    const pct = getMetricPercentage(raw);
    const pillar = EQ_PILLARS[code];
    const insight = getEQPillarInsight(code, pct, result.leadershipType);
    const benchmark = EQ_BENCHMARKS[code];
    return { code, pct, name: pillar.name, insight, benchmark };
  });

  const cultureDimData = (['T', 'PS', 'CQ', 'TS', 'ER'] as const).map((code) => {
    const raw = scores.culture[code] as number;
    const pct = getMetricPercentage(raw);
    const dim = CULTURE_DIMENSIONS[code];
    const insight = getCultureDimensionInsight(code, pct, result.leadershipFamily);
    return { code, pct, name: dim.name, fullName: dim.fullName, insight };
  });

  const bedData = [
    { code: 'B', label: 'Beliefs', raw: scores.bed.B, insight: bedInsights.beliefs },
    { code: 'EX', label: 'Excuses', raw: scores.bed.EX, insight: bedInsights.excuses },
    { code: 'D', label: 'Decisions', raw: scores.bed.D, insight: bedInsights.decisions },
  ].map((d) => ({ ...d, pct: getMetricPercentage(d.raw) }));

  return (
    <div className={`v2-report${isPrintMode ? ' print-mode' : ''}`}>
      <div id="v2-progress-bar" />

      <nav className="v2-nav">
        <div className="nav-brand">E.Q.U.I.P. 360</div>
        <ul className={`nav-links${mobileMenuOpen ? ' nav-links-open' : ''}`}>
          <li><a href="#v2-story" onClick={() => setMobileMenuOpen(false)}>Story</a></li>
          <li><a href="#v2-scores" onClick={() => setMobileMenuOpen(false)}>Scores</a></li>
          <li><a href="#v2-roi" onClick={() => setMobileMenuOpen(false)}>Leadership ROI</a></li>
          <li><a href="#v2-progression" onClick={() => setMobileMenuOpen(false)}>Next Level</a></li>
          <li><a href="#v2-mts" onClick={() => setMobileMenuOpen(false)}>Your Move</a></li>
        </ul>
        <button className="nav-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
          <span /><span /><span />
        </button>
      </nav>

      {/* HERO */}
      <section id="v2-hero">
        <div className="hero-bg" />
        <div className="v2-container">
          <div className="hero-grid">
            <div>
              <div className={`hero-meta reveal${heroVisible ? ' visible' : ''}`}>
                E.Q.U.I.P. 360 &nbsp;&middot;&nbsp; Leadership Intelligence &nbsp;&middot;&nbsp; {dateStr}
              </div>
              <h1 className={`hero-name reveal reveal-delay-1${heroVisible ? ' visible' : ''}`}>
                {firstName || 'Your'}<br />{lastName || 'Report'}
              </h1>
              <div className={`hero-title reveal reveal-delay-2${heroVisible ? ' visible' : ''}`}>
                Leadership Intelligence Assessment Report
              </div>
              <div className={`hero-badges reveal reveal-delay-3${heroVisible ? ' visible' : ''}`}>
                <span className="badge badge-gold">{leadershipFamily.name} Family</span>
                <span className="badge badge-blue">{leadershipType.name}</span>
                <span className="badge" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--v2-border2)', color: 'var(--v2-text2)' }}>
                  Overall: {overallPct}%
                </span>
              </div>
              <p className={`hero-tagline reveal reveal-delay-4${heroVisible ? ' visible' : ''}`}>
                "Leadership is emotional.<br />Because people are emotional."
              </p>
            </div>
            <div className={`hero-dial reveal reveal-delay-2${heroVisible ? ' visible' : ''}`}>
              <div className="dial-ring">
                <div className="dial-glow" />
                <svg width="240" height="240" viewBox="0 0 240 240">
                  <circle cx="120" cy="120" r="104" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="10" />
                  <AnimatedCircle cx={120} cy={120} r={104} stroke="url(#heroGrad)" strokeWidth={10}
                    dasharray={circumference104} targetOffset={getDialDashoffset(overallPct)} />
                  <defs>
                    <linearGradient id="heroGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#6B5520" />
                      <stop offset="100%" stopColor="#E2C068" />
                    </linearGradient>
                  </defs>
                </svg>
                <div className="dial-inner">
                  <HeroCounter target={overallPct} visible={heroVisible} />
                  <span className="dial-label">Overall Score</span>
                </div>
              </div>
              <div className={`dial-tier ${getTierChipClass(overallPct)}`}>{getTierLabel(overallPct)} Tier</div>
              <div className="hero-mini-scores">
                <div className="mini-score">
                  <div className="mini-score-val"><AnimatedNum target={eqPct} color={getTierColor(eqPct)} className="mini-score-val" />%</div>
                  <div className="mini-score-name">EQ Ready</div>
                </div>
                <div className="mini-score">
                  <div className="mini-score-val"><AnimatedNum target={bedPct} color={getTierColor(bedPct)} className="mini-score-val" />%</div>
                  <div className="mini-score-name">Behavioral</div>
                </div>
                <div className="mini-score">
                  <div className="mini-score-val"><AnimatedNum target={ciPct} color={getTierColor(ciPct)} className="mini-score-val" />%</div>
                  <div className="mini-score-name">Cultural</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="v2-divider" />

      {/* STAT HOOK */}
      <section id="v2-stat-hook">
        <div className="v2-container">
          <div className="section-eyebrow reveal" ref={observe}>Why This Matters</div>
          <div className="stat-hook-grid reveal reveal-delay-1" ref={observe}>
            <div className="stat-hook-item">
              <div className="stat-hook-num">10&times;</div>
              <div className="stat-hook-label">Leaders with high EQ are more than 10 times as likely to score high on critical success factors</div>
              <div className="stat-hook-source">Six Seconds Global EQ Report, 2024</div>
            </div>
            <div className="stat-hook-item">
              <div className="stat-hook-num">90%</div>
              <div className="stat-hook-label">of top performers have high emotional intelligence — it separates average leaders from elite ones</div>
              <div className="stat-hook-source">TalentSmart Research</div>
            </div>
            <div className="stat-hook-item">
              <div className="stat-hook-num">58%</div>
              <div className="stat-hook-label">of your job performance is directly influenced by your emotional intelligence score</div>
              <div className="stat-hook-source">Workforce EQ Studies</div>
            </div>
          </div>
        </div>
      </section>

      <div className="v2-divider" />

      {/* STORY */}
      <section id="v2-story">
        <div className="v2-container">
          <div className="section-eyebrow reveal" ref={observe}>Your Leadership Story</div>
          <div className="story-card reveal reveal-delay-1" ref={observe}>
            <p className="story-text" dangerouslySetInnerHTML={{ __html: leadershipStory }} />
          </div>
        </div>
      </section>

      <div className="v2-divider" />

      {/* IDENTITY */}
      <section id="v2-identity">
        <div className="v2-container">
          <div className="section-eyebrow reveal" ref={observe}>Leadership Identity</div>
          <div className="split-header reveal reveal-delay-1" ref={observe}>
            <div className="split-panel split-bright">
              <div className="split-tag"><span className="split-tag-dot" /> The Leader They See</div>
              <div className="split-heading">{leadershipType.name}</div>
              <div className="split-sub">When conditions are right, this is who shows up — the version your team remembers and follows.</div>
              <div className="trait-pills">
                {leadershipType.strengths.map((s, i) => (
                  <span key={i} className="trait-pill trait-pill-green">{s}</span>
                ))}
              </div>
            </div>
            <div className="split-panel split-dark">
              <div className="split-tag"><span className="split-tag-dot" /> The Leader Under Pressure</div>
              <div className="split-heading">{shadowName}</div>
              <div className="split-sub">When stakes rise, this version emerges — the one your team braces for.</div>
              <div className="trait-pills">
                {leadershipType.stressBehaviors.map((s, i) => (
                  <span key={i} className="trait-pill trait-pill-red">{s}</span>
                ))}
                {leadershipType.blindSpots.map((s, i) => (
                  <span key={`b${i}`} className="trait-pill trait-pill-amber">{s}</span>
                ))}
              </div>
            </div>
          </div>

          <div className="gap-meter-card reveal reveal-delay-2" ref={observe}>
            <div className="gap-meter-header">
              <div><div className="gap-meter-title">Identity vs. Reputation Gap</div></div>
              <div className="gap-meter-sub">The probable gap between how you see yourself and how your team experiences you in high-stakes moments.</div>
            </div>
            <div className="gap-visual">
              <div className="gap-label">How you see yourself</div>
              <div className="gap-track">
                <div className="gap-fill" style={{ background: 'linear-gradient(90deg, #2D6B52, var(--v2-green))', ['--target-width' as string]: `${gapData.selfPerception}%` }} />
              </div>
              <div className="gap-score" style={{ color: 'var(--v2-green)' }}>{gapData.selfPerception}%</div>
            </div>
            <div className="gap-visual">
              <div className="gap-label">How team likely sees you</div>
              <div className="gap-track">
                <div className="gap-fill" style={{ background: 'linear-gradient(90deg, #A03333, var(--v2-red))', ['--target-width' as string]: `${gapData.teamEstimate}%` }} />
              </div>
              <div className="gap-score" style={{ color: 'var(--v2-red)' }}>~{gapData.teamEstimate}%</div>
            </div>
            {gapData.gap > 0 && (
              <div className="gap-insight-box">
                <strong>The Gap Insight</strong>
                A {gapData.gap}-point gap is common but closeable. Every point you close on your pressure pattern score translates directly to a stronger reputation with your team.
              </div>
            )}
          </div>
        </div>
      </section>

      <div className="v2-divider" />

      {/* SCORES DASHBOARD */}
      <section id="v2-scores">
        <div className="v2-container">
          <div className="section-eyebrow reveal" ref={observe}>Score Dashboard</div>
          <div className="scores-trio reveal reveal-delay-1" ref={observe}>
            {[
              { name: 'Emotional Readiness', pct: eqPct, desc: 'How well you prepare and regulate yourself emotionally before leading', bench: BENCHMARKS.ER },
              { name: 'Behavioral Reality', pct: bedPct, desc: 'How your beliefs, excuses, and decisions actually show up under pressure', bench: BENCHMARKS.BR },
              { name: 'Cultural Influence', pct: ciPct, desc: 'How your emotional presence and energy shapes the people around you', bench: BENCHMARKS.CI },
            ].map((card) => (
              <div key={card.name} className="score-card-v2">
                <div className="top-accent" style={{ background: `linear-gradient(90deg, transparent, ${getTierColor(card.pct)}, transparent)` }} />
                <div className="score-card-name">{card.name}</div>
                <div className="arc-wrap">
                  <svg width="120" height="120" viewBox="0 0 120 120">
                    <circle cx="60" cy="60" r="52" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="7" />
                    <AnimatedCircle cx={60} cy={60} r={52} stroke={getTierColor(card.pct)} strokeWidth={7}
                      dasharray={circumference52} targetOffset={getArcDashoffset(card.pct)} />
                  </svg>
                  <div className="arc-center">
                    <AnimatedNum target={card.pct} color={getTierColor(card.pct)} className="arc-num" />
                    <span className="arc-pct">%</span>
                  </div>
                </div>
                <div className={`status-chip ${getTierChipClass(card.pct)}`}>{getTierLabel(card.pct)}</div>
                <div className="score-card-desc">{card.desc}</div>
                <div className="bench-note">Industry avg: {card.bench.low}–{card.bench.high}% &nbsp;&middot;&nbsp; You: {card.pct}%</div>
              </div>
            ))}
          </div>
          <div className="scores-connector reveal reveal-delay-2" ref={observe} dangerouslySetInnerHTML={{ __html: scoresConnector }} />
        </div>
      </section>

      <div className="v2-divider" />

      {/* EQ PILLARS */}
      <section id="v2-pillars">
        <div className="v2-container">
          <div className="section-eyebrow reveal" ref={observe}>EQ Pillars</div>
          <div className="pillar-card-v2 reveal reveal-delay-1" ref={observe}>
            <div className="pillar-list">
              {eqPillarData.map((p) => (
                <div key={p.code} className="pillar-item">
                  <div className="pillar-name-block">
                    <div className="pillar-name">{p.name}</div>
                    <div className="pillar-interp">{p.insight.substring(0, 60)}...</div>
                  </div>
                  <div className="pillar-bar-block">
                    <div className="bar-track">
                      <div className="bar-fill" style={{ background: getBarGradient(p.pct), ['--target-w' as string]: `${p.pct}%` }} />
                      <div className="bar-benchmark" style={{ left: `${p.benchmark}%` }} />
                    </div>
                  </div>
                  <div className="pillar-score-block">
                    <span className="pillar-pct" style={{ color: getTierColor(p.pct) }}>{p.pct}%</span>
                    <span className={`status-chip ${getTierChipClass(p.pct)}`} style={{ fontSize: '8px', padding: '2px 8px' }}>{getTierLabel(p.pct)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="pattern-insight reveal reveal-delay-2" ref={observe} dangerouslySetInnerHTML={{ __html: eqPattern }} />
        </div>
      </section>

      <div className="v2-divider" />

      {/* B.E.D. PROFILE */}
      <section id="v2-bed">
        <div className="v2-container">
          <div className="section-eyebrow reveal" ref={observe}>B.E.D. Profile</div>
          <div className="bed-grid reveal reveal-delay-1" ref={observe}>
            {bedData.map((d) => (
              <div key={d.code} className="bed-card-v2">
                <div className="bed-accent" style={{ background: `linear-gradient(90deg, transparent, ${getTierColor(d.pct)}, transparent)` }} />
                <div className="bed-card-label">{d.label}</div>
                <div className="bed-score-display" style={{ color: getTierColor(d.pct) }}><AnimatedNum target={d.pct} color={getTierColor(d.pct)} duration={1400} />%</div>
                <div className="bed-status">
                  <span className={`status-chip ${d.pct <= 10 ? 'chip-elite' : getTierChipClass(d.pct)}`} style={{ fontSize: '9px', padding: '3px 10px' }}>
                    {d.pct <= 10 ? 'Outlier' : getTierLabel(d.pct)}
                  </span>
                </div>
                <div className="bed-interp-text">{d.insight}</div>
                {d.code === 'EX' && d.pct <= 10 && (
                  <div className="bed-outlier-flag">This score needs closer examination — see Your Leadership Story</div>
                )}
              </div>
            ))}
          </div>
          <div className="bed-story-card reveal reveal-delay-2" ref={observe}>
            <div className="section-eyebrow" style={{ marginBottom: '16px' }}>Your B.E.D. Story</div>
            <p className="bed-story-text" dangerouslySetInnerHTML={{ __html: bedStory }} />
          </div>
        </div>
      </section>

      <div className="v2-divider" />

      {/* ROI */}
      <section id="v2-roi">
        <div className="v2-container">
          <div className="roi-header reveal" ref={observe}>
            <div className="section-eyebrow">Leadership ROI</div>
            <h2 className="roi-headline">What Your Scores<br />Mean for Your Business</h2>
            <p className="roi-sub">This is not abstract. Every point you move on these scores translates into measurable outcomes for your team, your culture, and your bottom line.</p>
          </div>
          <div className="roi-grid reveal reveal-delay-1" ref={observe}>
            {roiCards.map((card, i) => (
              <div key={i} className="roi-card">
                <div className={`roi-icon ${card.iconClass}`}>{card.icon}</div>
                <div>
                  <div className="roi-stat-num" style={{ color: card.statColor }}>{card.statNum}</div>
                  <div className="roi-stat-label">{card.label}</div>
                  <div className="roi-your-score" dangerouslySetInnerHTML={{ __html: card.yourScore.replace(/(\d+%)/g, '<strong>$1</strong>') }} />
                  <span className="roi-source">{card.source}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="roi-opportunity reveal reveal-delay-2" ref={observe}>
            <div className="roi-opp-title">Your Specific Opportunity</div>
            <p className="roi-opp-text">
              If you close your lowest layer gap over the next 90 days, the research is clear on what changes: your team's decision-making speed increases, your credibility under pressure strengthens, and your overall leadership impact compounds.
              <strong> That is not a soft outcome. That is a leadership ROI you can measure.</strong>
            </p>
          </div>
        </div>
      </section>

      <div className="v2-divider" />

      {/* CULTURE IMPACT */}
      <section id="v2-culture">
        <div className="v2-container">
          <div className="section-eyebrow reveal" ref={observe}>Culture Impact Index</div>
          <div className="culture-grid reveal reveal-delay-1" ref={observe}>
            {cultureDimData.map((d, idx) => (
              <div key={d.code} className="culture-metric" style={idx === cultureDimData.length - 1 ? { gridColumn: '1 / -1' } : undefined}>
                <div className="culture-donut">
                  <svg width="72" height="72" viewBox="0 0 72 72">
                    <circle cx="36" cy="36" r="30" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="5" />
                    <AnimatedCircle cx={36} cy={36} r={30} stroke={getTierColor(d.pct)} strokeWidth={5}
                      dasharray={circumference30} targetOffset={getDonutDashoffset(d.pct)} />
                  </svg>
                  <AnimatedNum target={d.pct} color={getTierColor(d.pct)} className="culture-donut-center" />
                </div>
                <div>
                  <div className="culture-metric-name">{d.fullName}</div>
                  <div className="culture-metric-interp">{d.insight}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="ripple-card reveal reveal-delay-2" ref={observe}>
            <p className="ripple-card-quote">{cultureRipple}</p>
            <div className="ripple-source">Culture Impact Analysis &nbsp;&middot;&nbsp; E.Q.U.I.P. 360</div>
          </div>
        </div>
      </section>

      <div className="v2-divider" />

      {/* PRESSURE PATTERN */}
      <section id="v2-pressure">
        <div className="v2-container">
          <div className="section-eyebrow reveal" ref={observe}>Pressure Pattern</div>
          <div className="pressure-card reveal reveal-delay-1" ref={observe}>
            <div className="pressure-2col">
              <div className="pressure-left">
                <h3>When the Heat Rises</h3>
                <p className="pressure-narrative">{pressureInsight}</p>
                <div className="behavior-list">
                  {leadershipType.stressBehaviors.map((b, i) => (
                    <div key={i} className="behavior-item"><span className="behavior-dot" />{b}</div>
                  ))}
                </div>
                <div className="team-sees">
                  <strong>What your team sees</strong>
                  A leader whose stress patterns become visible. The room gets quieter. People wait to see which version of you showed up today.
                </div>
              </div>
              <div className="pressure-right">
                <h4>Early Warning Signs</h4>
                <div className="warning-items">
                  {leadershipType.blindSpots.map((b, i) => (
                    <div key={i} className="warning-item">Watch for: {b}</div>
                  ))}
                </div>
                <div className="antidote-box">
                  <h4>The Antidote</h4>
                  <p dangerouslySetInnerHTML={{ __html: antidote.replace(/(".*?")/g, '<strong>$1</strong>') }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="v2-divider" />

      {/* PROGRESSION MAP */}
      <section id="v2-progression">
        <div className="v2-container">
          <div className="prog-header reveal" ref={observe}>
            <div className="section-eyebrow">Your Leadership Progression</div>
            <h2 className="prog-headline">Where You Are.<br />Where You're Going.</h2>
            <p className="prog-sub">Not just who you are today — but exactly what changes when you reach the next level.</p>
          </div>
          <div className="prog-track reveal reveal-delay-1" ref={observe}>
            <div className="prog-axis">
              <span className="prog-axis-label">0</span>
              <span className="prog-axis-label">Emerging (0–49)</span>
              <span className="prog-axis-label">Developing (50–69)</span>
              <span className="prog-axis-label">Strong (70–84)</span>
              <span className="prog-axis-label">Elite (85–100)</span>
            </div>
            <div className="prog-rail">
              <div className="prog-zones">
                <div className="prog-zone prog-zone-emerging" />
                <div className="prog-zone prog-zone-developing" />
                <div className="prog-zone prog-zone-strong" />
                <div className="prog-zone prog-zone-elite" />
              </div>
              <div className="prog-you" style={{ left: `${overallPct}%` }} />
              <div className="prog-marker" style={{ left: '50%', background: 'rgba(255,255,255,0.2)' }}>
                <div className="prog-marker-label">Industry Avg</div>
              </div>
              <div className="prog-marker" style={{ left: '80%', background: 'rgba(200,168,75,0.5)' }}>
                <div className="prog-marker-label">Top 20%</div>
              </div>
            </div>
          </div>
          <div className="prog-grid reveal reveal-delay-2" ref={observe}>
            <div className="prog-level prog-level-current">
              <div className="prog-level-tag">Current — {overallPct}% Overall</div>
              <div className="prog-level-score">{progressionData.currentTier}</div>
              <div className="prog-level-title">The Consistent Leader</div>
              <ul className="prog-level-items">
                <li>{ciPct >= 70 ? 'Strong' : 'Growing'} cultural presence</li>
                <li>Awareness and regulation are {eqPillarData[0].pct >= 70 && eqPillarData[1].pct >= 70 ? 'your anchors' : 'developing'}</li>
                <li>Drive is {eqPillarData[2].pct >= 70 ? 'consistent' : 'situational'}</li>
                <li>Under pressure: {leadershipType.stressBehaviors[0]?.toLowerCase()}</li>
              </ul>
            </div>
            <div className="prog-level prog-level-next">
              <div className="prog-level-tag">Next Level — {progressionData.nextTierThreshold}%+ Overall</div>
              <div className="prog-level-score">{progressionData.nextTier}</div>
              <div className="prog-level-title">The Transformational Leader</div>
              <ul className="prog-level-items">
                <li>Drive is internal — purpose-fueled, not mood-dependent</li>
                <li>Pressure triggers curiosity instead of reaction</li>
                <li>Accountability creates energy, not exhaustion</li>
                <li>People model their behavior after yours by choice</li>
              </ul>
            </div>
          </div>
          <div className="prog-distance reveal reveal-delay-3" ref={observe}>
            <div className="prog-distance-label">The Distance</div>
            <p className="prog-distance-text">
              You are <strong>{progressionData.pointsToNext} points away</strong> from the {progressionData.nextTier} tier. Leaders who implement their Move the Stool action consistently move an average of <span className="gold">8–12 points</span> across their lowest scoring pillar within 90 days. <strong>Retake this assessment in 90 days and track your trajectory.</strong>
            </p>
          </div>
        </div>
      </section>

      <div className="v2-divider" />

      {/* MOVE THE STOOL */}
      <section id="v2-mts">
        <div className="v2-container">
          <div className="mts-hero reveal" ref={observe}>
            <div className="mts-eyebrow">Move the Stool</div>
            <div className="mts-core-action">{mtsHeadline}</div>
            <p className="mts-para">{moveTheStool}</p>
          </div>
          <div className="reveal reveal-delay-1" ref={observe} style={{ marginBottom: '20px' }}>
            <div className="section-eyebrow" style={{ marginBottom: '20px' }}>Your 30-Day Challenge</div>
            <div className="mts-actions">
              {actionItems.map((item) => (
                <div key={item.num} className="action-v2">
                  <div className="action-num-v2">{item.num}</div>
                  <div>
                    <div className="action-headline-v2">{item.headline}</div>
                    <div className="action-desc-v2">{item.desc}</div>
                    <span className="action-tied">{item.tied}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="v2-divider" />

      {/* BECAUSE */}
      <section id="v2-because">
        <div className="v2-container">
          <div className="because-wrap reveal" ref={observe}>
            <div className="section-eyebrow" style={{ justifyContent: 'center' }}>Your Leadership Because</div>
            <span className="because-glyph">&ldquo;</span>
            <p className="because-statement" dangerouslySetInnerHTML={{
              __html: becauseStatement.replace(/BECAUSE/g, '<em>because</em>')
            }} />
            <div className="because-attr">{fullName || 'Leader'} &nbsp;&middot;&nbsp; {leadershipType.name}</div>
          </div>
        </div>
      </section>

      <div className="v2-divider" />

      {/* FOOTER */}
      <footer className="v2-footer">
        <div className="v2-container">
          <div className="footer-inner">
            <div className="footer-brand">
              <strong>E.Q.U.I.P. 360</strong>
              equip360.io &nbsp;&middot;&nbsp; Powered by Mick Unplugged Leadership Intelligence<br />
              Report generated {dateStr}
            </div>
            <div className="footer-actions">
              {authUser && (
                <Link to="/dashboard" className="footer-cta">Dashboard</Link>
              )}
              <button className="footer-cta-outline" onClick={handleDownloadPDF} disabled={generatingPdf}>
                {generatingPdf ? 'Generating...' : 'Download PDF'}
              </button>
              <Link to="/" className="footer-cta-outline">Home</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

const printMode = new URLSearchParams(window.location.search).get('print') === '1';

function HeroCounter({ target, visible }: { target: number; visible: boolean }) {
  const value = useCountUp(target, 1200, printMode || visible);
  if (printMode) return <span className="dial-number">{target}</span>;
  return <span className="dial-number">{value}</span>;
}

function AnimatedNum({ target, color, className, duration = 1200 }: { target: number; color?: string; className?: string; duration?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [triggered, setTriggered] = useState(printMode);
  const value = useCountUp(target, duration, triggered);

  useEffect(() => {
    if (printMode || !ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTriggered(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  if (printMode) return <span ref={ref} className={className} style={color ? { color } : undefined}>{target}</span>;
  return <span ref={ref} className={className} style={color ? { color } : undefined}>{value}</span>;
}

function AnimatedCircle({ cx, cy, r, stroke, strokeWidth, dasharray, targetOffset, className }: {
  cx: number; cy: number; r: number; stroke: string; strokeWidth: number;
  dasharray: number; targetOffset: number; className?: string;
}) {
  const ref = useRef<SVGCircleElement>(null);
  const [animated, setAnimated] = useState(printMode);

  useEffect(() => {
    if (printMode || !ref.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimated(true);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <circle
      ref={ref}
      cx={cx} cy={cy} r={r}
      fill="none"
      stroke={stroke}
      strokeWidth={strokeWidth}
      strokeDasharray={dasharray}
      strokeDashoffset={animated ? targetOffset : dasharray}
      strokeLinecap="round"
      className={className}
      style={printMode ? undefined : { transition: 'stroke-dashoffset 1.4s cubic-bezier(0.4,0,0.2,1) 0.2s' }}
    />
  );
}

export default ResultsPage;
